import { SmartLink } from './SmartLink'
import { useOptionalT } from '../i18n/LocaleProvider'
import type { LinkTarget } from './types'

export interface ServiceCardProps extends LinkTarget {
  image: string
  /**
   * Describes the picture. Defaults to the generic `Service Image` the placeholders have
   * always carried; pass one wherever the image is a real photograph and means something.
   */
  imageAlt?: string
  title: string
  descr: string
  ctaLabel?: string
  /**
   * `inner` adds the `ul-service--inner` modifier used on the services listing page.
   * The home-page slider uses the bare card. The inner markup is identical.
   */
  variant?: 'default' | 'inner'
}

/**
 * A `.ul-service` card. Shared by the `services` listing (6 cards, `--inner`
 * modifier) and the home page's services slider (bare).
 *
 * The target comes from `LinkTarget`: several of the Foundation's areas of work are now
 * whole apps on their own subdomains, so a card points either at a route (`to`) or at
 * another app (`href`).
 */
export function ServiceCard({
  image,
  imageAlt: imageAltProp,
  title,
  descr,
  to,
  href,
  ctaLabel: ctaLabelProp,
  variant = 'default',
}: ServiceCardProps) {
  const t = useOptionalT()
  const imageAlt = imageAltProp ?? t('ul.serviceCard.imageAlt')
  const ctaLabel = ctaLabelProp ?? t('ul.serviceCard.cta')
  return (
    <div className={variant === 'inner' ? 'ul-service ul-service--inner' : 'ul-service'}>
      <div className="ul-service-img">
        <img src={image} alt={imageAlt} />
      </div>
      <div className="ul-service-txt">
        <h3 className="ul-service-title">
          <SmartLink to={to} href={href}>
            {title}
          </SmartLink>
        </h3>
        <p className="ul-service-descr">{descr}</p>
        <SmartLink to={to} href={href} className="ul-service-btn">
          <i className="flaticon-up-right-arrow" /> {ctaLabel}
        </SmartLink>
      </div>
    </div>
  )
}
